import { useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import API from "@/lib/api";

/**
 * Bridges AuthContext ↔ ThemeContext to persist a user's theme choice
 * server-side on `users.theme_preference`, so the same staff user gets
 * the same theme when logging in from a different device.
 *
 * - On login, if server has `theme_preference`, apply it locally.
 * - When theme changes (after login), PUT it to /api/profile/theme.
 * - No-op for unauthenticated visitors (they fall back to localStorage).
 */
export default function ThemeSync() {
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const lastPushedTheme = useRef(null);
  const appliedForUser = useRef(null);
  // /auth/me returns `_id` (Mongo doc) but login responses return `id` — accept either.
  const userId = user && typeof user === "object" ? (user._id || user.id) : null;

  // When a user logs in, pull their server-side preference.
  useEffect(() => {
    if (!userId) {
      appliedForUser.current = null;
      lastPushedTheme.current = null;
      return;
    }
    if (appliedForUser.current === userId) return;
    appliedForUser.current = userId;
    const serverTheme = user?.theme_preference;
    if (serverTheme === "dark" || serverTheme === "light") {
      if (serverTheme !== theme) setTheme(serverTheme);
      lastPushedTheme.current = serverTheme;
    } else {
      lastPushedTheme.current = theme;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, user?.theme_preference]);

  // When theme changes post-login, sync it to the server.
  useEffect(() => {
    if (!userId) return;
    if (lastPushedTheme.current === theme) return;
    lastPushedTheme.current = theme;
    API.put("/profile/theme", { theme }).catch(() => { /* silent */ });
  }, [theme, userId]);

  return null;
}
